import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from "react";
import type { AdminIdentity, AdminSession } from "@shudian/contracts";
import { configureCloudApiClient } from "../api/http.js";
import { adminLogin as apiAdminLogin, adminLogout as apiAdminLogout } from "../api/admin.js";

const STORAGE_KEY = "shudian.cloudAdmin.session.v1";

interface PersistedSession {
  admin: AdminIdentity;
  accessToken: string;
  expiresAt: string;
}

function loadPersistedSession(): PersistedSession | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedSession;
    if (!parsed.accessToken || !parsed.expiresAt) return null;
    if (new Date(parsed.expiresAt).getTime() <= Date.now()) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function persistSession(session: PersistedSession | null): void {
  if (!session) {
    sessionStorage.removeItem(STORAGE_KEY);
    return;
  }
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

// 模块加载阶段就注册一次 token accessor，确保任何在 AdminAuthProvider useEffect
// 之前发出的请求（含子组件首次渲染立刻发起的请求）也能带上 Authorization。
// onUnauthorized 留空：在 Provider 真正挂上之前不应该清 session，避免误判。
configureCloudApiClient({
  getAdminToken: () => loadPersistedSession()?.accessToken ?? null,
  onUnauthorized: () => {
    /* placeholder; AdminAuthProvider mount 后会替换为真正的清理逻辑 */
  }
});

export interface AdminAuthContextValue {
  session: PersistedSession | null;
  isAuthenticated: boolean;
  login: (input: { email: string; password: string }) => Promise<AdminSession>;
  logout: () => Promise<void>;
}

export const AdminAuthContext = createContext<AdminAuthContextValue | null>(null);

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<PersistedSession | null>(() => loadPersistedSession());
  const sessionRef = useRef<PersistedSession | null>(session);

  useEffect(() => {
    configureCloudApiClient({
      getAdminToken: () => sessionRef.current?.accessToken ?? null,
      onUnauthorized: () => {
        sessionRef.current = null;
        persistSession(null);
        setSession(null);
      }
    });
  }, []);

  const login = useCallback(async (input: { email: string; password: string }) => {
    const result = await apiAdminLogin(input);
    const persisted: PersistedSession = {
      admin: result.admin,
      accessToken: result.accessToken,
      expiresAt: result.expiresAt
    };
    sessionRef.current = persisted;
    persistSession(persisted);
    setSession(persisted);
    return result;
  }, []);

  const logout = useCallback(async () => {
    try {
      if (session?.accessToken) {
        await apiAdminLogout();
      }
    } catch {
      // ignore logout errors; we still clear local session below
    } finally {
      sessionRef.current = null;
      persistSession(null);
      setSession(null);
    }
  }, [session]);

  const value = useMemo<AdminAuthContextValue>(
    () => ({
      session,
      isAuthenticated: !!session,
      login,
      logout
    }),
    [session, login, logout]
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}
